import { z } from 'zod';

/**
 * Wave 29 — Mão-de-obra diária (per-role labor tracking).
 *
 * One row = one (date, role) bucket on a project. Multiple rows per day
 * per project is the common case (pedreiros + serventes + carpinteiros
 * all working the same slab).
 *
 * Why a pinned role enum instead of free text?
 *   Reports need to aggregate "pedreiros" across projects, months, and
 *   PMs. Free text means one PM writes "pedreiro", another writes
 *   "pedreiro de alvenaria", a third writes "mason". The enum forces
 *   the common vocabulary; `outro` is the escape hatch for the long
 *   tail (pintor especializado, sondagem, etc.).
 */

export const laborRoleSchema = z.enum([
  'pedreiro',
  'servente',
  'carpinteiro',
  'armador',
  'eletricista',
  'encanador',
  'pintor',
  'gesseiro',
  'azulejista',
  'mestre_obra',
  'encarregado',
  'outro',
]);
export type LaborRole = z.infer<typeof laborRoleSchema>;

/**
 * Canonical display order for role rollups + Pareto charts.
 *
 * Supervisory roles first (mestre_obra, encarregado) because on a
 * Brazilian site report the eyes go there first — they are the most
 * expensive headcount and the accountability trail. Then craft roles
 * by how common they are across residential/commercial builds. `outro`
 * always last — it's the escape hatch, not a primary category.
 *
 * This MUST match the SQL `ORDER_BY` CASE in `labor.service.ts` so the
 * table and the chart never disagree.
 */
export const LABOR_ROLES: readonly LaborRole[] = [
  'mestre_obra',
  'encarregado',
  'pedreiro',
  'servente',
  'carpinteiro',
  'armador',
  'eletricista',
  'encanador',
  'pintor',
  'gesseiro',
  'azulejista',
  'outro',
] as const;

export const laborEntrySchema = z.object({
  id: z.string(),
  projectId: z.string(),
  logDate: z.string(), // YYYY-MM-DD
  role: laborRoleSchema,
  headcount: z.number().int().nonnegative(),
  hoursPerPerson: z.number().nonnegative(),
  hourlyRateCents: z.number().int().nonnegative(),
  /** headcount * hoursPerPerson — convenience, always derivable. */
  totalHours: z.number().nonnegative(),
  /** headcount * hoursPerPerson * hourlyRateCents — convenience. */
  totalCostCents: z.number().int().nonnegative(),
  notes: z.string().nullable(),
  createdBy: z.string(),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type LaborEntry = z.infer<typeof laborEntrySchema>;

export const createLaborEntryRequestSchema = z.object({
  logDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/u, 'Data deve estar no formato YYYY-MM-DD'),
  role: laborRoleSchema,
  headcount: z.number().int().min(0).max(999),
  hoursPerPerson: z.number().min(0).max(24),
  hourlyRateCents: z.number().int().min(0).max(100_000_00), // ≤ R$ 100k/h — sanity cap
  notes: z.string().trim().max(500).optional(),
});
export type CreateLaborEntryRequest = z.infer<
  typeof createLaborEntryRequestSchema
>;

export const updateLaborEntryRequestSchema = z
  .object({
    logDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/u)
      .optional(),
    role: laborRoleSchema.optional(),
    headcount: z.number().int().min(0).max(999).optional(),
    hoursPerPerson: z.number().min(0).max(24).optional(),
    hourlyRateCents: z.number().int().min(0).max(100_000_00).optional(),
    notes: z.string().trim().max(500).nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, {
    message: 'Informe ao menos um campo para atualizar',
  });
export type UpdateLaborEntryRequest = z.infer<
  typeof updateLaborEntryRequestSchema
>;

/**
 * Aggregate rollup for the labor report page. One bucket per role,
 * plus project-wide totals.
 */
export const laborRoleRollupSchema = z.object({
  role: laborRoleSchema,
  headcountPersonDays: z.number().nonnegative(),
  totalHours: z.number().nonnegative(),
  totalCostCents: z.number().int().nonnegative(),
  /** Share of project cost, 0..1. */
  costShare: z.number(),
});
export type LaborRoleRollup = z.infer<typeof laborRoleRollupSchema>;

export const laborSummarySchema = z.object({
  totalHours: z.number().nonnegative(),
  totalCostCents: z.number().int().nonnegative(),
  entryCount: z.number().int().nonnegative(),
  personDays: z.number().nonnegative(),
  byRole: z.array(laborRoleRollupSchema),
  /** From/to bounds observed in the dataset; null when empty. */
  firstDate: z.string().nullable(),
  lastDate: z.string().nullable(),
});
export type LaborSummary = z.infer<typeof laborSummarySchema>;

export const listLaborResponseSchema = z.object({
  entries: z.array(laborEntrySchema),
  summary: laborSummarySchema,
});
export type ListLaborResponse = z.infer<typeof listLaborResponseSchema>;
